import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Collection et document pour le stock
const STOCK_COLLECTION = 'inventory';
export const FLASHCARD_PRODUCT_ID = 'flashcard-terminale';

// Interface pour le stock
interface StockItem {
  id: string;
  name: string;
  initialStock: number;
  currentStock: number;
  isAvailable: boolean;
  lastUpdated: Date;
}

// Valeurs par défaut pour le stock
const DEFAULT_STOCK: StockItem = {
  id: FLASHCARD_PRODUCT_ID,
  name: 'Flashcards Notions-Définitions SES Terminale',
  initialStock: 985,
  currentStock: 985,
  isAvailable: true,
  lastUpdated: new Date()
};

/**
 * Initialise le stock d'un produit s'il n'existe pas déjà
 */
export const initializeStock = async (productId: string, productName: string, initialStock: number): Promise<void> => {
  try {
    // Utiliser directement Firestore
    const stockRef = doc(db, STOCK_COLLECTION, productId);
    const stockDoc = await getDoc(stockRef);
    
    if (!stockDoc.exists()) {
      await setDoc(stockRef, {
        id: productId,
        name: productName,
        initialStock: initialStock,
        currentStock: initialStock,
        isAvailable: true,
        lastUpdated: new Date()
      });
      console.log('Stock initialized in Firestore directly');
    } else {
      console.log('Stock already exists in Firestore');
    }
  } catch (error) {
    console.error('Error initializing stock:', error);
    // Ne pas propager l'erreur pour éviter de bloquer l'interface
  }
};

/**
 * Récupère le stock actuel d'un produit
 */
export const getProductStock = async (productId: string): Promise<StockItem> => {
  try {
    // Utiliser directement Firestore
    const stockRef = doc(db, STOCK_COLLECTION, productId);
    const stockDoc = await getDoc(stockRef);
    
    if (stockDoc.exists()) {
      const data = stockDoc.data();
      return {
        id: data.id || productId,
        name: data.name || 'Flashcards Notions-Définitions SES Terminale',
        initialStock: data.initialStock || 985,
        currentStock: data.currentStock || 985,
        isAvailable: data.isAvailable !== undefined ? data.isAvailable : true,
        lastUpdated: data.lastUpdated?.toDate() || new Date()
      };
    }
    
    // Si le document n'existe pas, l'initialiser
    await initializeStock(productId, 'Flashcards Notions-Définitions SES Terminale', 985);
    
    // Retourner les valeurs par défaut
    return DEFAULT_STOCK;
  } catch (error) {
    console.error('Error getting product stock:', error);
    // En cas d'erreur, retourner les valeurs par défaut
    return DEFAULT_STOCK;
  }
};

/**
 * Vérifie si un produit est disponible
 */
export const isProductAvailable = async (productId: string): Promise<boolean> => {
  try {
    const stock = await getProductStock(productId);
    return stock.isAvailable;
  } catch (error) {
    console.error('Error checking product availability:', error);
    // En cas d'erreur, supposer que le produit est disponible
    return true;
  }
};

/**
 * Initialise le stock des flashcards si nécessaire
 */
export const initializeFlashcardStock = async (): Promise<void> => {
  await initializeStock(FLASHCARD_PRODUCT_ID, 'Flashcards Notions-Définitions SES Terminale', 985);
};

/**
 * Met à jour le stock d'un produit
 */
export const updateProductStock = async (productId: string, newStock: number): Promise<void> => {
  try {
    console.log(`Updating stock for ${productId} to ${newStock}`);
    
    // Mettre à jour directement dans Firestore
    const stockRef = doc(db, STOCK_COLLECTION, productId);
    
    await updateDoc(stockRef, {
      currentStock: newStock,
      isAvailable: newStock > 0,
      lastUpdated: new Date()
    });
    
    console.log(`Stock updated successfully: ${newStock} items`);
  } catch (error) {
    console.error('Error updating product stock:', error);
    throw error;
  }
};

/**
 * Simule un achat pour tester la décrémentation du stock
 * Cette fonction ne doit être utilisée que pour les tests ou par l'administrateur
 */
export const simulatePurchase = async (): Promise<void> => {
  try {
    console.log('Simulating purchase...');
    
    // Récupérer le stock actuel
    const stock = await getProductStock(FLASHCARD_PRODUCT_ID);
    
    // Décrémenter le stock
    const newStock = Math.max(0, stock.currentStock - 1);
    
    // Mettre à jour le stock
    await updateProductStock(FLASHCARD_PRODUCT_ID, newStock);
    
    console.log(`Purchase simulated directly in Firestore. New stock: ${newStock}`);
  } catch (error) {
    console.error('Error simulating purchase:', error);
    throw error;
  }
};